import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useSnackbar } from 'notistack'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import {
  GuardaPrivacidade,
  mensagemErroPrivacidade,
  rotuloAtor,
  useAcessoPrivacidade,
} from '@/components/privacidade/comum'
import { privacidadeService } from '@/services/privacidadeService'
import {
  ALVO_RETENCAO_LABEL,
  AlvoRetencaoLegal,
  NovaRetencaoLegalRequest,
  RetencaoLegal,
} from '@/types/privacidade'
import { formatDateTime } from '@/utils/formatters'

const ALVOS: AlvoRetencaoLegal[] = ['DOCUMENTO', 'RESERVA', 'CNPJ']

const AJUDA_ALVO: Record<AlvoRetencaoLegal, string> = {
  DOCUMENTO: 'Id (uuid) do documento.',
  RESERVA: 'Id (uuid) da reserva — cobre todos os documentos dela.',
  CNPJ: 'CNPJ (só dígitos) — cobre todas as reservas do cliente.',
}

const NOVA: NovaRetencaoLegalRequest = { alvoTipo: 'DOCUMENTO', alvoRef: '', motivo: '', referenciaProcesso: '' }

const Conteudo: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const acesso = useAcessoPrivacidade()
  const [filtroAlvo, setFiltroAlvo] = useState<AlvoRetencaoLegal | ''>('')
  const [somenteAtivas, setSomenteAtivas] = useState(true)
  const [lista, setLista] = useState<RetencaoLegal[] | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [nova, setNova] = useState<NovaRetencaoLegalRequest | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [liberando, setLiberando] = useState<RetencaoLegal | null>(null)
  const [motivoLiberacao, setMotivoLiberacao] = useState('')

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setLista(
        await privacidadeService.retencoesLegais({
          alvoTipo: filtroAlvo || undefined,
          ativas: somenteAtivas ? true : undefined,
        })
      )
    } catch (e) {
      setErro(mensagemErroPrivacidade(e, 'Falha ao carregar as retenções legais'))
      setLista(null)
    } finally {
      setCarregando(false)
    }
  }, [filtroAlvo, somenteAtivas])

  useEffect(() => {
    carregar()
  }, [carregar])

  const criar = async () => {
    if (!nova) return
    setSalvando(true)
    try {
      const alvoRef = nova.alvoTipo === 'CNPJ' ? nova.alvoRef.replace(/\D/g, '') : nova.alvoRef.trim()
      const criada = await privacidadeService.criarRetencaoLegal({
        alvoTipo: nova.alvoTipo,
        alvoRef,
        motivo: nova.motivo.trim(),
        referenciaProcesso: nova.referenciaProcesso?.trim() || undefined,
      })
      const bloqueados = criada?.expurgosBloqueados ?? []
      enqueueSnackbar(
        bloqueados.length > 0
          ? `Retenção legal registrada — expurgos bloqueados: ${bloqueados.join(', ')}`
          : 'Retenção legal registrada',
        { variant: 'success' }
      )
      setNova(null)
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroPrivacidade(e, 'Falha ao registrar a retenção legal'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const liberar = async () => {
    if (!liberando) return
    setSalvando(true)
    try {
      await privacidadeService.liberarRetencaoLegal(liberando.id, motivoLiberacao.trim())
      enqueueSnackbar('Retenção legal liberada', { variant: 'success' })
      setLiberando(null)
      setMotivoLiberacao('')
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroPrivacidade(e, 'Falha ao liberar'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Retenção legal (legal hold)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Retenção legal sempre prevalece sobre expurgo: bloqueia a entrada na fila, as aprovações e a execução.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNova({ ...NOVA })} sx={{ flexShrink: 0 }}>
          Nova retenção legal
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Alvo"
          value={filtroAlvo}
          onChange={(e) => setFiltroAlvo(e.target.value as AlvoRetencaoLegal | '')}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {ALVOS.map((a) => (
            <MenuItem key={a} value={a}>
              {ALVO_RETENCAO_LABEL[a]}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch checked={somenteAtivas} onChange={(e) => setSomenteAtivas(e.target.checked)} />}
          label="Somente ativas"
        />
      </Stack>

      {carregando && !lista ? (
        <EstadoDeLista estado="carregando" />
      ) : erro ? (
        <EstadoDeLista estado="erro" detalhe={erro} acao={{ label: 'Tentar de novo', onClick: carregar }} />
      ) : lista && lista.length === 0 ? (
        <EstadoDeLista estado="vazio" mensagem="Nenhuma retenção legal" detalhe="Nada retido neste filtro." />
      ) : lista ? (
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" aria-label="Retenções legais">
            <TableHead>
              <TableRow>
                <TableCell>Alvo</TableCell>
                <TableCell>Referência</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell>Processo</TableCell>
                <TableCell>Criada</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((r) => {
                const ativa = r.ativa ?? !r.liberadaEm
                return (
                  <TableRow key={r.id}>
                    <TableCell>{ALVO_RETENCAO_LABEL[r.alvoTipo] ?? r.alvoTipo}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.alvoRef}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>{r.motivo}</TableCell>
                    <TableCell>{r.referenciaProcesso || '—'}</TableCell>
                    <TableCell>
                      {formatDateTime(r.criadaEm)} · {rotuloAtor(r.criadaPor, acesso.usuarioId)}
                    </TableCell>
                    <TableCell>
                      {ativa ? (
                        <Chip size="small" color="error" label="Ativa" />
                      ) : (
                        <Tooltip title={r.motivoLiberacao ?? ''}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Liberada em ${formatDateTime(r.liberadaEm)} por ${rotuloAtor(r.liberadaPor, acesso.usuarioId)}`}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {ativa &&
                        (acesso.dpo ? (
                          <Button size="small" onClick={() => setLiberando(r)}>
                            Liberar
                          </Button>
                        ) : (
                          <Tooltip title="Somente o DPO libera retenção legal">
                            <span>
                              <Button size="small" disabled>
                                Liberar
                              </Button>
                            </span>
                          </Tooltip>
                        ))}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      <Dialog open={!!nova} onClose={() => (salvando ? undefined : setNova(null))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Nova retenção legal</DialogTitle>
        {nova && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                label="Alvo"
                value={nova.alvoTipo}
                onChange={(e) => setNova({ ...nova, alvoTipo: e.target.value as AlvoRetencaoLegal })}
              >
                {ALVOS.map((a) => (
                  <MenuItem key={a} value={a}>
                    {ALVO_RETENCAO_LABEL[a]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Referência do alvo"
                required
                value={nova.alvoRef}
                helperText={AJUDA_ALVO[nova.alvoTipo]}
                onChange={(e) => setNova({ ...nova, alvoRef: e.target.value })}
              />
              <TextField
                label="Motivo"
                required
                multiline
                rows={3}
                value={nova.motivo}
                onChange={(e) => setNova({ ...nova, motivo: e.target.value })}
                inputProps={{ maxLength: 500 }}
              />
              <TextField
                label="Referência do processo (opcional)"
                inputProps={{ maxLength: 120 }}
                value={nova.referenciaProcesso ?? ''}
                onChange={(e) => setNova({ ...nova, referenciaProcesso: e.target.value })}
              />
              {!acesso.dpo && (
                <Alert severity="info">Depois de criada, somente o DPO pode liberar esta retenção.</Alert>
              )}
            </Stack>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNova(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={criar}
            disabled={salvando || !nova?.alvoRef.trim() || !nova?.motivo.trim()}
          >
            {salvando ? 'Salvando…' : 'Registrar retenção'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!liberando} onClose={() => (salvando ? undefined : setLiberando(null))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Liberar retenção legal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {liberando && `${ALVO_RETENCAO_LABEL[liberando.alvoTipo]} ${liberando.alvoRef}`}. Expurgos que estavam
            bloqueados por esta retenção continuam bloqueados até nova aprovação dupla.
          </Typography>
          <TextField
            label="Motivo da liberação"
            required
            fullWidth
            multiline
            rows={3}
            value={motivoLiberacao}
            onChange={(e) => setMotivoLiberacao(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLiberando(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={liberar} disabled={salvando || !motivoLiberacao.trim()}>
            Liberar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

/** Retenção legal (AN-12 §6): criar — DPO ou ADMIN; liberar — só DPO, motivo obrigatório. */
export const RetencoesLegaisPage: FC = () => (
  <GuardaPrivacidade>
    <Conteudo />
  </GuardaPrivacidade>
)

export default RetencoesLegaisPage
